import React, { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Target, Heart, Compass, BookOpen, MapPin, Mail, Phone, MessageCircle, Users as UsersIcon, Crown, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const VALUES = [
  { icon: Target, title: "Rigour First", desc: "Method, evidence and reproducibility are non-negotiable in every project we touch." },
  { icon: Heart, title: "Ethics Always", desc: "Authorship, attribution, ethical AI use and integrity drive everything we publish." },
  { icon: Compass, title: "Mentor-Led", desc: "Every researcher is paired with a domain expert who has shipped real scholarship." },
  { icon: BookOpen, title: "Globally Presentable", desc: "We optimise for Q1–Q2 placement, indexing, and citation-worthy contributions." },
];

const KIND_LABELS = { company: "Company", division: "Division", team: "Team", squad: "Squad" };

// ---- MemberCard ---------------------------------------------------------
const MemberCard = ({ m, featured = false }) => (
  <div
    className={`kb-card overflow-hidden ${featured ? "ring-2 ring-kb-primary/50 shadow-lg" : ""}`}
    data-testid={`about-member-${(m.name || m.user_id || "x").split(" ").join("-").toLowerCase()}`}
  >
    <div className={`aspect-square ${featured ? "bg-gradient-to-br from-kb-primary/10 to-kb-subtle" : "bg-kb-subtle"} flex items-center justify-center relative`}>
      {featured && (
        <span className="absolute top-2 left-2 kb-mono text-[9px] uppercase tracking-wider bg-kb-primary text-white px-2 py-0.5 rounded-sm inline-flex items-center gap-1">
          <Crown className="w-3 h-3" /> Head
        </span>
      )}
      {m.picture ? (
        <img src={m.picture} alt={m.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-20 h-20 rounded-full bg-white border border-kb-divider text-kb-purple flex items-center justify-center text-2xl font-bold">
          {m.initials}
        </div>
      )}
    </div>
    <div className="p-5">
      <div className="font-serif text-lg text-kb-ink leading-tight">{m.name}</div>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-purple mt-2">{m.role_name}</div>
      {m.affiliation && <div className="text-[11px] text-kb-ink-light mt-1">{m.affiliation}</div>}
    </div>
  </div>
);

// ---- UnitBlock — non-recursive, flat block for one org unit ----------
// Rendered ONCE per unit; parents pass a pre-flattened list from the top so
// there's no self-reference in JSX (which was tripping babel-loader with
// "Maximum call stack size exceeded" during traversal).
function UnitBlock({ unit, depth = 0 }) {
  const hasContent = !!unit.head || unit.members.length > 0;
  if (!hasContent) return null;
  const nonHeadMembers = unit.members.filter((m) => !unit.head || m.user_id !== unit.head.user_id);
  return (
    <div
      data-testid={`about-unit-${unit.id}`}
      className={`${depth === 0 ? "border-t border-slate-200 pt-10" : "pl-4 sm:pl-6 lg:pl-8 border-l-2 border-kb-primary/20 ml-2 mt-8"}`}
    >
      <div className="flex items-baseline flex-wrap gap-3">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">
          {KIND_LABELS[unit.kind] || "Team"}
        </div>
        <h3 className={`${depth === 0 ? "font-serif text-2xl lg:text-3xl" : "font-serif text-xl lg:text-2xl"} text-kb-ink`}>
          {unit.name}
        </h3>
        {unit.members.length > 0 && (
          <span className="kb-mono text-[10px] text-kb-ink-muted">
            · {unit.members.length} member{unit.members.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {unit.description && (
        <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl leading-relaxed">{unit.description}</p>
      )}
      {(unit.head || nonHeadMembers.length > 0) && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {unit.head && <MemberCard m={unit.head} featured />}
          {nonHeadMembers.map((m) => <MemberCard key={m.user_id || m.name} m={m} />)}
        </div>
      )}
    </div>
  );
}

// ---- Breadcrumb (top-level unit index) ---------------------------------
const OrgIndex = ({ roots }) => {
  if (roots.length <= 1) return null;
  return (
    <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="about-org-index">
      <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mr-1">Jump to:</span>
      {roots.map((r) => (
        <a
          key={r.id}
          href={`#about-unit-${r.id}`}
          className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary border border-kb-primary/30 px-2 py-1 rounded-sm hover:bg-kb-primary hover:text-white transition-colors inline-flex items-center gap-1"
        >
          {r.name} <ChevronRight className="w-3 h-3" />
        </a>
      ))}
    </div>
  );
};

export default function About() {
  const [tree, setTree] = useState(null);   // org units from /public/org-tree
  const [curated, setCurated] = useState(null); // team_roles that aren't captured by a unit

  useEffect(() => {
    api.get("/public/org-tree").then((r) => setTree(r.data || [])).catch(() => setTree([]));
    api.get("/team").then((r) => setCurated(r.data || [])).catch(() => setCurated([]));
  }, []);

  // Group curated members by team_section, but only sections that DON'T
  // already exist as an org unit (case-insensitive match). This keeps
  // legacy team_roles data visible without duplicating it.
  const uncategorised = useMemo(() => {
    if (!Array.isArray(curated) || !Array.isArray(tree)) return [];
    const unitNames = new Set((tree || []).map((u) => (u.name || "").toLowerCase()));
    const groups = [];
    const idx = new Map();
    for (const m of curated) {
      const section = (m.team_section || "").trim();
      if (section && unitNames.has(section.toLowerCase())) continue;
      const label = section || "Core Team";
      if (!idx.has(label)) { idx.set(label, groups.length); groups.push({ section: label, members: [] }); }
      groups[idx.get(label)].members.push(m);
    }
    return groups;
  }, [curated, tree]);

  // Pre-flatten the tree with a plain DFS so the JSX never self-references
  // UnitBlock (avoids the babel-loader "Maximum call stack size" bug).
  const flat = useMemo(() => {
    if (!Array.isArray(tree)) return [];
    const byParent = new Map();
    for (const u of tree) {
      const k = u.parent_unit_id || "__root__";
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k).push(u);
    }
    const out = [];
    const seen = new Set();
    const walk = (id, depth) => {
      for (const u of (byParent.get(id) || [])) {
        if (seen.has(u.id)) continue; // cycle guard
        seen.add(u.id);
        out.push({ unit: u, depth });
        walk(u.id, depth + 1);
      }
    };
    walk("__root__", 0);
    return out;
  }, [tree]);
  const roots = flat.filter((x) => x.depth === 0).map((x) => x.unit);

  const loading = tree === null || curated === null;
  const empty = !loading && flat.length === 0 && uncategorised.length === 0;

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="about-page">
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28 lg:pb-24">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">ABOUT THE LAB</div>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-kb-ink leading-[1.05]">
              A Nepal-grown research<br />
              <em className="italic text-kb-primary">operating system</em>.
            </h1>
            <p className="mt-8 text-lg text-kb-ink-muted leading-relaxed">
              KhojByte (खोजबाइट) was founded with one belief — that brilliant ideas often go
              unpublished not for lack of merit, but for lack of mentorship, infrastructure and
              clarity. We&apos;ve built the lab we wish we had as graduate students: a place where
              mentor-led research meets modern publication tooling.
            </p>
            <p className="mt-5 text-base text-kb-ink-muted leading-relaxed">
              From our base in Kirtipur, Kathmandu, we serve researchers and institutions across South Asia
              — keeping ethics at the centre, and using technology to compress the time from
              idea to publication.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-slate-200 rounded-sm overflow-hidden bg-cover bg-center aspect-[4/5]" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1650099661667-479cd5882390)" }} />
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">VALUES</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-kb-ink max-w-3xl leading-tight">
            What guides every project we accept.
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white p-7" data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <v.icon className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                <div className="font-serif text-lg text-kb-ink mt-5">{v.title}</div>
                <div className="text-sm text-kb-ink-muted mt-2 leading-relaxed">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------- Hierarchical team ------- */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="about-team-section">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">ORGANISATION</div>
        <h2 className="font-serif text-3xl lg:text-4xl text-kb-ink max-w-3xl leading-tight">
          A hierarchy of <em className="italic text-kb-primary">domain leads</em><br />
          across every unit.
        </h2>
        <p className="text-sm text-kb-ink-muted mt-4 max-w-2xl leading-relaxed">
          Each team is led by a domain head who&apos;s shipped real scholarship, industry work or teaching.
          Explore the full hierarchy below — heads are highlighted, and every unit rolls up to its parent.
        </p>

        {loading ? (
          <div className="mt-12 kb-mono text-xs text-kb-ink-muted">Loading team…</div>
        ) : empty ? (
          <div className="mt-12 text-center py-16 border border-dashed border-kb-divider rounded-md bg-white" data-testid="about-team-empty">
            <UsersIcon className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
            <h3 className="font-serif text-lg text-kb-ink mt-4">Team roster coming soon</h3>
            <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">Our public team profiles are being finalised. Watch this space.</p>
          </div>
        ) : (
          <>
            <OrgIndex roots={roots} />
            <div className="mt-8">
              {flat.map(({ unit, depth }) => (
                <div id={`about-unit-${unit.id}`} key={unit.id}>
                  <UnitBlock unit={unit} depth={depth} />
                </div>
              ))}
            </div>

            {/* Any curated team_roles whose team_section doesn't map to a unit */}
            {uncategorised.length > 0 && (
              <div className="mt-16 space-y-14 border-t border-slate-200 pt-10">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Featured Roles</div>
                {uncategorised.map((g) => (
                  <div key={g.section} data-testid={`about-uncategorised-${g.section.toLowerCase().replace(/\s+/g, '-')}`}>
                    <h3 className="font-serif text-2xl text-kb-ink">{g.section}</h3>
                    <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      {g.members.map((m) => <MemberCard key={m.role_id} m={m} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section id="contact" className="border-t border-slate-200 bg-kb-subtle">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">CONTACT</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-kb-ink leading-tight">
              Talk to a research <em className="italic text-kb-primary">consultant</em>.
            </h2>
            <p className="mt-4 text-kb-ink-muted">
              Have a paper to publish, an idea to validate, or a department-wide programme to plan?
              Reach out on WhatsApp for the fastest response.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid="contact-grid">
              <div className="bg-white p-6">
                <MapPin className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-4">Office</div>
                <div className="text-sm text-kb-ink mt-1 leading-relaxed">
                  Tyanglaphat Chowk,<br />Kirtipur, Kathmandu,<br />Nepal
                </div>
              </div>
              <div className="bg-white p-6">
                <Phone className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-4">Phone</div>
                <div className="text-sm text-kb-ink mt-1">+977 9851423235</div>
                <a href="https://wa.me/9779851423235" target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-2 bg-[#25D366] text-white text-xs font-medium px-3 py-2 rounded-sm hover:bg-[#1faa53]"
                  data-testid="about-whatsapp-cta">
                  <MessageCircle className="w-3.5 h-3.5" /> Chat on WhatsApp
                </a>
              </div>
              <div className="bg-white p-6">
                <Mail className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-4">Email</div>
                <div className="text-sm text-kb-ink mt-1">hello@khojbyte.research</div>
              </div>
              <div className="bg-white p-6">
                <div className="w-5 h-5 rounded-full border-2 border-kb-primary" />
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-4">Working hours</div>
                <div className="text-sm text-kb-ink mt-1">Sunday — Friday<br />09:00 – 18:00 NPT</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
